import { LevelDesignerComponent } from '../../level-designer/level-designer.component';
import { PropertyEditor } from '../property-editor';
import { PropertyInput } from './input-base';

export interface SelectOption {
    value: string | number | null;
    label: string;
}

export class SelectInput extends PropertyInput {
    protected dropdownVisible: boolean = false;
    protected highlightedOption: number = -1;
    protected options: SelectOption[];
    protected optionsMethod?: (() => SelectOption[]) | undefined;
    protected allowTextEditing: boolean = false;
    protected cursorPosition: number = 0;
    protected isSelected: boolean = false;
    protected textOffset: number = 0;
    protected searchText: string = '';
    protected scrollOffset: number = 0;
    protected scrollbarWidth: number = 12;
    protected isDraggingScrollbar: boolean = false;
    protected scrollbarStartY: number = 0;
    protected scrollbarStartOffset: number = 0;
    protected scrollbarHeight: number = 0;
    protected scrollbarY: number = 0;
    protected scrollbarThumbHeight: number = 0;
    protected scrollbarThumbY: number = 0;

    constructor(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        value: any,
        isActive: boolean,
        optionsObj: SelectOption[] | (() => SelectOption[]),
        levelDesigner: LevelDesignerComponent,
        propertyEditor: PropertyEditor,
        allowTextEditing: boolean = false
    ) {
        super(ctx, x, y, width, height, value, isActive, levelDesigner, propertyEditor);
        if (typeof optionsObj === 'function') {
            this.optionsMethod = optionsObj;
            this.options = optionsObj();
            this.displayValue = this.options.find(o => o.value == this.value)?.label ?? this.displayValue;
        } else {
            this.options = optionsObj;
            this.displayValue = this.options.find(o => o.value == this.value)?.label ?? this.displayValue;
        }
        this.allowTextEditing = allowTextEditing;
        this.searchText = String(this.displayValue);
    }

    override draw(): void {
        this.drawBackground();
        
        // Save context for clipping
        this.ctx.save();
        
        // Create clipping region for text
        this.ctx.beginPath();
        this.ctx.rect(this.x + 5, this.y + 2, this.width - 10, this.height - 4);
        this.ctx.clip();

        // Draw text
        this.ctx.fillStyle = '#000000';
        this.ctx.font = '12px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';

        if (this.isActive && this.isSelected && this.allowTextEditing) {
            // Draw selection
            const textWidth = this.ctx.measureText(this.searchText).width;
            this.ctx.fillStyle = '#0078D7';
            this.ctx.fillRect(this.x + 5 - this.textOffset, this.y + 2, textWidth, this.height - 4);
            this.ctx.fillStyle = '#FFFFFF';
        }

        // Draw text with offset
        this.ctx.fillText(this.searchText, this.x + 5 - this.textOffset, this.y + this.height / 2);

        // Draw cursor if active and not selected
        if (this.isActive && !this.isSelected && this.allowTextEditing) {
            this.drawCursor();
        }

        // Restore context (removes clipping)
        this.ctx.restore();

        // Draw dropdown arrow
        this.drawDropdownArrow();
        
        // Draw dropdown if visible
        if (this.dropdownVisible) {
            this.drawDropdown();
        }
    }

    drawDropdown(): void {
        if (!this.dropdownVisible) return;

        const optionHeight = 25;
        const maxVisibleOptions = 10;
        const actualOptions = Math.min(this.options.length, maxVisibleOptions);
        const dropdownHeight = actualOptions * optionHeight;
        const totalHeight = this.options.length * optionHeight;
        const scrollbarVisible = this.options.length > maxVisibleOptions;

        // Draw dropdown background
        this.ctx.fillStyle = '#FFFFFF';
        this.ctx.strokeStyle = '#CCCCCC';
        this.ctx.lineWidth = 1;
        this.ctx.fillRect(this.x, this.y + this.height, this.width, dropdownHeight);
        this.ctx.strokeRect(this.x, this.y + this.height, this.width, dropdownHeight);

        // Save context for clipping
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.rect(this.x, this.y + this.height, this.width - (scrollbarVisible ? this.scrollbarWidth : 0), dropdownHeight);
        this.ctx.clip();

        // Draw options
        this.options.forEach((option, index) => {
            const optionY = this.y + this.height + (index * optionHeight) - this.scrollOffset;
            
            // Only draw if within visible area
            if (optionY >= this.y + this.height && optionY < this.y + this.height + dropdownHeight) {
                // Highlight if this option is selected
                if (index === this.highlightedOption) {
                    this.ctx.fillStyle = '#E8E8E8';
                    this.ctx.fillRect(this.x, optionY, this.width - (scrollbarVisible ? this.scrollbarWidth : 0), optionHeight);
                }
                
                this.ctx.fillStyle = '#000000';
                this.ctx.fillText(option.label, this.x + 5, optionY + optionHeight / 2);
            }
        });

        // Restore context (removes clipping)
        this.ctx.restore();

        // Draw scrollbar if needed
        if (scrollbarVisible) {
            this.drawScrollbar(dropdownHeight, totalHeight);
        }
    }

    protected drawScrollbar(dropdownHeight: number, totalHeight: number): void {
        const scrollbarX = this.x + this.width - this.scrollbarWidth;
        const scrollbarTop = this.y + this.height;
        
        // Calculate scrollbar dimensions
        const scrollbarHeight = dropdownHeight;
        const scrollbarThumbHeight = Math.max(30, (dropdownHeight / totalHeight) * dropdownHeight);
        const scrollableHeight = totalHeight - dropdownHeight;
        const scrollbarTrackHeight = scrollbarHeight - scrollbarThumbHeight;
        
        // Calculate thumb position
        const scrollbarThumbY = scrollbarTop + (this.scrollOffset / scrollableHeight) * scrollbarTrackHeight;
        
        // Draw scrollbar track
        this.ctx.fillStyle = '#F0F0F0';
        this.ctx.fillRect(scrollbarX, scrollbarTop, this.scrollbarWidth, scrollbarHeight);
        
        // Draw scrollbar thumb
        this.ctx.fillStyle = '#C0C0C0';
        this.ctx.fillRect(scrollbarX, scrollbarThumbY, this.scrollbarWidth, scrollbarThumbHeight);
        
        // Draw scrollbar borders
        this.ctx.strokeStyle = '#CCCCCC';
        this.ctx.strokeRect(scrollbarX, scrollbarTop, this.scrollbarWidth, scrollbarHeight);
        
        // Store scrollbar dimensions for click handling
        this.scrollbarHeight = scrollbarHeight;
        this.scrollbarY = scrollbarTop;
        this.scrollbarThumbHeight = scrollbarThumbHeight;
        this.scrollbarThumbY = scrollbarThumbY;
    }

    protected drawDropdownArrow(): void {
        this.ctx.beginPath();
        const arrowX = this.x + this.width - 15;
        const arrowY = this.y + this.height / 2;
        this.ctx.moveTo(arrowX, arrowY - 2);
        this.ctx.lineTo(arrowX + 5, arrowY + 3);
        this.ctx.lineTo(arrowX + 10, arrowY - 2);
        this.ctx.strokeStyle = '#666666';
        this.ctx.stroke();
    }

    protected drawCursor(): void {
        const cursorX = this.x + 5 + this.ctx.measureText(this.searchText.slice(0, this.cursorPosition)).width - this.textOffset;
        
        // Only draw cursor if it's within the visible area
        if (cursorX >= this.x + 5 && cursorX <= this.x + this.width - 5) {
            this.ctx.beginPath();
            this.ctx.moveTo(cursorX, this.y + 4);
            this.ctx.lineTo(cursorX, this.y + this.height - 4);
            this.ctx.strokeStyle = '#000000';
            this.ctx.lineWidth = 1;
            this.ctx.stroke();
        }
    }

    protected ensureCursorVisible(): void {
        if (!this.isActive) return;

        const cursorX = this.ctx.measureText(this.searchText.slice(0, this.cursorPosition)).width;
        const visibleWidth = this.width - 10; // Account for padding

        // If cursor would be off-screen to the right
        if (cursorX - this.textOffset > visibleWidth) {
            this.textOffset = cursorX - visibleWidth + 5; // Add small padding
        }
        // If cursor would be off-screen to the left
        else if (cursorX - this.textOffset < 0) {
            this.textOffset = cursorX;
        }

        // Ensure we don't scroll past the start of the text
        this.textOffset = Math.max(0, this.textOffset);

        // Ensure we don't scroll past the end of the text
        const textWidth = this.ctx.measureText(this.searchText).width;
        const maxOffset = Math.max(0, textWidth - visibleWidth);
        this.textOffset = Math.min(this.textOffset, maxOffset);
    }

    override handleClick(x: number, y: number): boolean {
        // Check if click is within input bounds or dropdown bounds
        const isInInputBounds = x >= this.x && x <= this.x + this.width && 
                               y >= this.y && y <= this.y + this.height;
        
        const optionHeight = 25;
        const maxVisibleOptions = 10;
        const actualOptions = Math.min(this.options.length, maxVisibleOptions);
        const dropdownHeight = actualOptions * optionHeight;
        
        const dropdownBounds = this.dropdownVisible ? {
            x: this.x,
            y: this.y + this.height,
            width: this.width,
            height: dropdownHeight
        } : null;

        const isInDropdownBounds = dropdownBounds && 
            x >= dropdownBounds.x && x <= dropdownBounds.x + dropdownBounds.width &&
            y >= dropdownBounds.y && y <= dropdownBounds.y + dropdownBounds.height;

        // Check if click is on scrollbar
        const isOnScrollbar = this.dropdownVisible && this.options.length > maxVisibleOptions &&
            x >= this.x + this.width - this.scrollbarWidth &&
            x <= this.x + this.width &&
            y >= this.y + this.height &&
            y <= this.y + this.height + dropdownHeight;

        // Handle scrollbar dragging
        if (isOnScrollbar) {
            if (y >= this.scrollbarThumbY && y <= this.scrollbarThumbY + this.scrollbarThumbHeight) {
                this.isDraggingScrollbar = true;
                this.scrollbarStartY = y;
                this.scrollbarStartOffset = this.scrollOffset;
                return true;
            }
            // Click on scrollbar track
            const scrollableHeight = (this.options.length - maxVisibleOptions) * optionHeight;
            const scrollbarTrackHeight = this.scrollbarHeight - this.scrollbarThumbHeight;
            const clickRatio = (y - this.y - this.height) / scrollbarTrackHeight;
            this.scrollOffset = clickRatio * scrollableHeight;
            this.scrollOffset = Math.max(0, Math.min(this.scrollOffset, scrollableHeight));
            return true;
        }

        // If not active, only respond to clicks in input bounds
        if (!this.isActive) {
            if (isInInputBounds) {
                this.focus();
                return true;
            }
            return false;
        }

        // If active and dropdown is visible
        if (this.dropdownVisible) {
            if (isInDropdownBounds) {
                const optionIndex = Math.floor((y - (this.y + this.height) + this.scrollOffset) / optionHeight);
                if (optionIndex >= 0 && optionIndex < this.options.length) {
                    // Intentionally keep the raw option value (do NOT .toString()) so that select-input can support a "None" option that sets a real null value.
                    const newValue = this.options[optionIndex].value;
                    // Only update if value has changed
                    if (this.value !== newValue) {
                        this.value = newValue;
                        // Immediately commit the value to the form
                        this.propertyEditor.commitValueFromInput(this, this.value);
                    }
                    this.displayValue = this.options[optionIndex].label;
                    this.searchText = String(this.displayValue);
                    this.dropdownVisible = false;
                    
                    // Ensure redraw happens to hide the dropdown
                    if (this.levelDesigner) {
                        this.levelDesigner.redraw();
                    }
                    
                    return true;
                }
            }
            // Only close dropdown if clicking outside both input and dropdown
            if (!isInInputBounds && !isInDropdownBounds) {
                this.dropdownVisible = false;
                
                // Ensure redraw happens to hide the dropdown
                if (this.levelDesigner) {
                    this.levelDesigner.redraw();
                }
                
                return true;
            }
            return true;
        }

        // If active but dropdown not visible, handle text input click if enabled
        if (isInInputBounds) {
            if (this.allowTextEditing) {
                // Calculate cursor position based on click location
                const clickX = x - (this.x + 5) + this.textOffset;
                let minDistance = Number.MAX_VALUE;
                let bestPosition = 0;

                // Find the character position closest to the click
                for (let i = 0; i <= this.searchText.length; i++) {
                    const charX = this.ctx.measureText(this.searchText.slice(0, i)).width;
                    const distance = Math.abs(charX - clickX);
                    if (distance < minDistance) {
                        minDistance = distance;
                        bestPosition = i;
                    }
                }

                this.cursorPosition = bestPosition;
                this.isSelected = false;
                this.ensureCursorVisible();
            } else {
                // If text editing is not allowed, just show dropdown
                this.dropdownVisible = true;
                this.highlightedOption = -1;
            }
            
            // Ensure redraw happens
            if (this.levelDesigner) {
                this.levelDesigner.redraw();
            }
            
            return true;
        }

        return false;
    }

    override handleKeyEvent(event: KeyboardEvent): boolean {
        if (!this.isActive) return false;

        // Handle text editing if enabled
        if (this.allowTextEditing) {
            // Always prevent propagation for backspace and delete keys when input is active
            if (event.key === 'Backspace' || event.key === 'Delete') {
                event.stopPropagation();
                event.preventDefault();
            }

            let handled = false;

            // Ensure cursor position is within bounds
            this.cursorPosition = Math.min(Math.max(0, this.cursorPosition), this.searchText.length);

            if (this.isSelected && event.key.length === 1) {
                this.searchText = event.key;
                this.cursorPosition = 1;
                this.isSelected = false;
                this.onSearchTextChanged();
                handled = true;
            } else {
                switch (event.key) {
                    case 'ArrowLeft':
                        if (this.isSelected) {
                            this.isSelected = false;
                            this.cursorPosition = 0;
                        } else if (this.cursorPosition > 0) {
                            this.cursorPosition--;
                        }
                        handled = true;
                        break;
                    case 'ArrowRight':
                        if (this.isSelected) {
                            this.isSelected = false;
                            this.cursorPosition = this.searchText.length;
                        } else if (this.cursorPosition < this.searchText.length) {
                            this.cursorPosition++;
                        }
                        handled = true;
                        break;
                    case 'Home':
                        event.preventDefault();
                        this.cursorPosition = 0;
                        this.isSelected = false;
                        handled = true;
                        break;
                    case 'End':
                        event.preventDefault();
                        this.cursorPosition = this.searchText.length;
                        this.isSelected = false;
                        handled = true;
                        break;
                    case 'Backspace':
                        if (this.isSelected) {
                            this.searchText = '';
                            this.cursorPosition = 0;
                            this.isSelected = false;
                            this.onSearchTextChanged();
                        } else if (this.cursorPosition > 0) {
                            this.searchText = this.searchText.slice(0, this.cursorPosition - 1) + this.searchText.slice(this.cursorPosition);
                            this.cursorPosition--;
                            this.onSearchTextChanged();
                        }
                        handled = true;
                        break;
                    case 'Delete':
                        if (this.isSelected) {
                            this.searchText = '';
                            this.cursorPosition = 0;
                            this.isSelected = false;
                            this.onSearchTextChanged();
                        } else if (this.cursorPosition < this.searchText.length) {
                            this.searchText = this.searchText.slice(0, this.cursorPosition) + this.searchText.slice(this.cursorPosition + 1);
                            this.onSearchTextChanged();
                        }
                        handled = true;
                        break;
                    case 'Escape':
                        this.dropdownVisible = false;
                        handled = true;
                        break;
                    default:
                        if (event.key.length === 1) {
                            this.searchText = this.searchText.slice(0, this.cursorPosition) + event.key + this.searchText.slice(this.cursorPosition);
                            this.cursorPosition++;
                            this.onSearchTextChanged();
                            handled = true;
                        }
                }
            }

            // Ensure cursor position is within bounds after any changes
            this.cursorPosition = Math.min(Math.max(0, this.cursorPosition), this.searchText.length);

            if (handled) {
                this.ensureCursorVisible();
            }

            return handled;
        }

        // Handle dropdown navigation if visible
        if (this.dropdownVisible) {
            switch (event.key) {
                case 'ArrowDown':
                    this.highlightedOption = Math.min(
                        this.highlightedOption + 1,
                        this.options.length - 1
                    );
                    return true;
                case 'ArrowUp':
                    this.highlightedOption = Math.max(this.highlightedOption - 1, 0);
                    return true;
                case 'Enter':
                    if (this.highlightedOption >= 0) {
                        // Intentionally keep the raw option value (do NOT .toString()) so that select-input can support a "None" option that sets a real null value.
                        const newValue = this.options[this.highlightedOption].value;
                        // Only update if value has changed
                        if (this.value !== newValue) {
                            this.value = newValue;
                            this.displayValue = this.options[this.highlightedOption].label;
                            this.searchText = String(this.displayValue);
                            // Immediately commit the value to the form
                            this.propertyEditor.commitValueFromInput(this, this.value);
                        }
                        this.dropdownVisible = false;
                        return true;
                    }
                    break;
                case 'Escape':
                    this.dropdownVisible = false;
                    return true;
            }
        }
        return false;
    }
    
    override focus(): void {
        this.isActive = true;
        this.isSelected = true;  // Always select all text on focus
        this.dropdownVisible = true;
        this.highlightedOption = -1;
        if (this.levelDesigner) {
            this.levelDesigner.redraw();
        }
    }

    override blur(): void {
        this.isActive = false;
        this.dropdownVisible = false;
        this.highlightedOption = -1;
        this.isSelected = false;
        this.textOffset = 0;
        this.scrollOffset = 0;
        this.isDraggingScrollbar = false;
    }

    protected onSearchTextChanged(): void {
        // Override this in derived classes to handle search text changes
    }

    override setValue(value: any): void {
        super.setValue(value);
        this.searchText = String(this.displayValue);
        this.ensureCursorVisible();
    }

    override handleMouseMove(x: number, y: number): boolean {
        if (this.isDraggingScrollbar) {
            const deltaY = y - this.scrollbarStartY;
            const scrollableHeight = (this.options.length - 10) * 25; // 10 is maxVisibleOptions
            const scrollbarTrackHeight = this.scrollbarHeight - this.scrollbarThumbHeight;
            const scrollRatio = deltaY / scrollbarTrackHeight;
            
            this.scrollOffset = this.scrollbarStartOffset + (scrollRatio * scrollableHeight);
            this.scrollOffset = Math.max(0, Math.min(this.scrollOffset, scrollableHeight));
            
            if (this.levelDesigner) {
                this.levelDesigner.redraw();
            }
            return true;
        }
        return false;
    }

    override handleMouseUp(x: number, y: number): boolean {
        if (this.isDraggingScrollbar) {
            this.isDraggingScrollbar = false;
            return true;
        }
        return false;
    }
} 
